// agentClientActions — the one place that knows what a server-requested client
// action MEANS.
//
// A tool on aos-mcp may return `client: { action, ...payload }`; the agent loop
// forwards it verbatim as a `client-action` SSE event and knows no action names
// itself. This module is the other half of that seam: a dispatch table, not a
// chain of `if (name === …)` branches in the panel — the same shape as
// assetFolderPath's folderUpdaterFor, and for the same reason. An unknown
// action is REPORTED, never silently ignored: the server thought it was asking
// the app to do something, and a no-op would leave the user watching a tool
// succeed while nothing happened.
//
// Handlers return a user-facing sentence (rendered in the transcript) or null
// for "nothing worth saying". They may be async; a rejection is caught by the
// caller and shown as an error.

import { requestFocus, FOCUS_REASONS } from './graphFocus.js'
import { describeLocation } from './graphNodeSearch.js'

/** True when this agent event is a server-requested client action. */
export function isClientActionEvent(event) {
  return event?.type === 'client-action' && typeof event.action === 'string' && !!event.action
}

const HANDLERS = {
  // focus_node found exactly one node and wants the editor to show it.
  'focus-node': async (payload) => {
    const result = await requestFocus(payload)
    if (result.ok) {
      const where = describeLocation({ graph: payload.graph === 'events' ? 'event' : payload.graph, container: payload.container })
      return `Showing ${payload.label ?? payload.nodeId} — ${where}.`
    }
    if (result.reason === FOCUS_REASONS.superseded) return null
    // The honest failure: the agent read the blueprint SAVED on the server, the
    // editor is showing the local one. Say which, rather than sitting still.
    return `Could not show ${payload.label ?? payload.nodeId} — it is not in the graph open here. `
      + 'The agent reads the saved blueprint; load from cloud if your editor is behind.'
  },
}

/** Every action this build understands (for tests and diagnostics). */
export const CLIENT_ACTIONS = Object.freeze(Object.keys(HANDLERS))

/**
 * Run a client action.
 * @param {{action: string, payload?: object}} event
 * @returns {Promise<string|null>} a sentence for the transcript, or null
 * @throws on an unknown action — a silent drop is the failure mode this exists
 *         to prevent.
 */
export function runClientAction(event) {
  const handler = HANDLERS[event?.action]
  if (!handler) {
    return Promise.reject(new Error(
      `this build does not understand the agent action "${event?.action}" (known: ${CLIENT_ACTIONS.join(', ')})`,
    ))
  }
  return Promise.resolve(handler(event.payload ?? {}))
}
